/**
 * Scroll Utility Functions
 * Provides various scrolling methods for different scenarios
 */

/**
 * Scrolls to the top of the main content area
 * @param {boolean} smooth - Whether to use smooth scrolling animation
 */
export function scrollToTop(smooth = true) {
    const scrollOptions = smooth ? { top: 0, behavior: 'smooth' } : { top: 0 };
    
    // Method 1: Try to find the main content container (.c-main)
    const mainContainer = document.querySelector('.c-main');
    if (mainContainer) {
        mainContainer.scrollTo(scrollOptions);
        return true;
    }
    
    // Method 2: Try common scrollable containers
    const scrollableContainers = [
        '.c-body',
        '.container-fluid',
        '[class*="container"]',
        '[data-simplebar]',
        '.main-content',
        '.content-wrapper'
    ];
    
    for (const selector of scrollableContainers) {
        const container = document.querySelector(selector);
        if (container && (container.scrollHeight > container.clientHeight)) {
            container.scrollTo(scrollOptions);
            return true;
        }
    }
    
    // Method 3: Fallback to window scroll
    window.scrollTo(scrollOptions);
    return true;
}

/**
 * Scrolls to a specific element
 * @param {string|Element} target - CSS selector or DOM element
 * @param {Object} options - Scroll options (offset, smooth, etc.)
 */
export function scrollToElement(target, options = {}) {
    const {
        offset = 0,
        smooth = true,
        behavior = smooth ? 'smooth' : 'auto'
    } = options;
    
    const element = typeof target === 'string' ? document.querySelector(target) : target;
    
    if (!element) {
        console.warn('Element not found for scrolling:', target);
        return false;
    }
    
    // Find the scrollable parent
    let scrollableParent = element.parentElement;
    while (scrollableParent) {
        const style = window.getComputedStyle(scrollableParent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            break;
        }
        scrollableParent = scrollableParent.parentElement;
    }
    
    // If no scrollable parent found, use window
    const container = scrollableParent || window;
    
    const elementRect = element.getBoundingClientRect();
    const containerRect = container === window 
        ? { top: 0, left: 0 } 
        : container.getBoundingClientRect();
    
    const scrollTop = container === window 
        ? window.pageYOffset 
        : container.scrollTop;
    
    const targetPosition = scrollTop + elementRect.top - containerRect.top - offset;
    
    if (container === window) {
        window.scrollTo({
            top: targetPosition,
            behavior
        });
    } else {
        container.scrollTo({
            top: targetPosition,
            behavior
        });
    }
    
    return true;
}

/**
 * Scrolls to show flash messages
 * This is specifically designed for notification areas
 */
export function scrollToFlashMessages() {
    // Common selectors for flash message containers
    const flashSelectors = [
        '.alert',
        '.toast',
        '.notification',
        '[role="alert"]',
        '.flash-message',
        '.c-alert'
    ];
    
    for (const selector of flashSelectors) {
        const flashElement = document.querySelector(selector);
        if (flashElement) {
            return scrollToElement(flashElement, { offset: 20 });
        }
    }
    
    // Fallback to general scroll to top
    return scrollToTop(true);
}

/**
 * Auto-scroll handler for AJAX requests
 * Can be used as an interceptor or called manually
 */
export function autoScrollOnAjax() {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
        scrollToTop(true);
    }, 150);
}

export default {
    scrollToTop,
    scrollToElement,
    scrollToFlashMessages,
    autoScrollOnAjax
};